import { Icon } from "@iconify/react";
import Classnames from "classnames";
import React, { useEffect, useRef, useState } from "react";

import useClipboard from "@hooks/use-clipboard";

import i18n from "../utils/i18n";
import Button from "./button";
import style from "./textbox.styl";

type TextboxProps = {
  /**
   * Set which sides should have no border radius.
   *
   * Used to 'attach' to buttons or other components.
   *
   * Array of values. Values are one of: `top`, `right`, `bottom`, `left`
   *
   * @default []
   */
  attach?: Array<"top" | "right" | "bottom" | "left">;
  /**
   * Set initial hasBlurred state to true (used to show errors without having to blur)
   */
  autoBlur?: boolean;
  /**
   * Set focus on page load
   */
  autoFocus?: boolean;
  blurOnFocus?: boolean;
  className?: string;
  /**
   * Add copy to clipboard button
   */
  copy?: boolean;
  copyButton?: boolean;
  disabled?: boolean;
  /**
   * Fill parent width 100%
   */
  fill?: boolean;
  hint?: React.ReactNode;
  label?: string;
  /**
   * Show "(optional)" text next to the label
   */
  optional?: boolean;
  placeholder?: string;
  /**
   * Prefix icon
   */
  prefixIcon?: React.ReactNode;
  readOnly?: boolean;
  /**
   * Suffix icon
   */
  suffixIcon?: React.ReactNode;
  /**
   * When not focused, shrink to icon (only works when prefixIcon is specified)
   */
  shrink?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (value?: string | number) => void;
  onEnter?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLDivElement>) => void;
  /**
   * @default 'large'
   */
  size?: "small" | "medium" | "large";
  /**
   * Show border even on readOnly textbox
   */
  showBorder?: boolean;
  /**
   * Textbox input type
   *
   * @default 'text'
   */
  type?: string;
  /**
   * Name attribute for the input element
   */
  name?: string;
  value?: string | number;
  error?: React.ReactNode;
  success?: boolean;
  normalStyle?: boolean;
  unBlur?: boolean;
  autocomplete?: string;
};

const Textbox = React.forwardRef<HTMLDivElement, TextboxProps>(
  (
    {
      attach = [],
      size = "large",
      type = "text",
      autoBlur,
      autoFocus,
      blurOnFocus,
      className,
      copy: copyProp,
      copyButton,
      disabled,
      fill,
      hint,
      label,
      optional,
      placeholder,
      prefixIcon,
      readOnly,
      suffixIcon,
      shrink,
      onBlur: onBlurProp,
      onChange: onChangeProp,
      onEnter,
      onFocus: onFocusProp,
      onKeyDown: onKeyDownProp,
      onMouseEnter,
      onMouseLeave,
      showBorder,
      name,
      value: valueProp,
      error,
      success,
      normalStyle,
      unBlur,
      autocomplete,
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [hasBlurred, setBlurred] = useState(autoBlur);
    const [hasFocus, setFocus] = useState<boolean>();
    const [value, setValue] = useState(valueProp);
    const [isComposing, setComposing] = useState<boolean>(); // This is used to prevent onEnter callback being called when writing Japanese
    let internalRef = useRef<HTMLDivElement>(null);
    if (ref) internalRef = ref as React.MutableRefObject<HTMLDivElement>;

    const [copied, copy] = useClipboard(value?.toString() ?? "");

    useEffect(() => {
      if (valueProp !== value) {
        setValue(valueProp);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valueProp]);

    useEffect(() => {
      if (unBlur) setBlurred(undefined);
    }, [unBlur]);

    const classNameMerged = Classnames(
      style.textbox,
      style[size],
      {
        [style.focus]: hasFocus,
        [style.disabled]: disabled,
        [style.hasError]: error && hasBlurred,
        [style.hasPreIcon]: prefixIcon,
        [style.hasSufIcon]: suffixIcon,
        [style.readonly]: readOnly && !normalStyle,
        [style.showBorder]: showBorder,
        [style.shrink]: shrink && prefixIcon && !value,
        [style.success]: success,
        [style.noLabel]: typeof label === "undefined",
        [style.left]: copyButton,
      },
      ...attach.map((attachItem) => style[attachItem]),
      className,
    );

    const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setBlurred(true);
      setFocus(undefined);
      if (typeof onBlurProp === "function") {
        onBlurProp(e);
      }
    };

    const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocus(true);
      if (typeof onFocusProp === "function") {
        onFocusProp(e);
      }

      if (blurOnFocus) {
        setBlurred(true);
      }
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let textValue: string | number = e.target.value;
      if (type === "number") {
        textValue = Number(textValue);
      }
      setValue(textValue);

      // Safari does some weird clearing of the input before composition end, so prevent this from setting bad values.
      if (isComposing) return;

      if (typeof onChangeProp === "function") {
        onChangeProp(textValue);
      }
    };

    const copyButtonElement = copyButton ? (
      <Button attach={["right"]} size={size} onClick={() => copy()}>
        {copied ? <Icon icon="feather:check" /> : <Icon icon="feather:copy" />}
        {i18n.t("action:copy")}
      </Button>
    ) : undefined;

    const suffixIconElement = copyProp ? (
      <Button className={style.copyButton} as="span" onClick={() => copy()}>
        {copied ? <Icon icon="feather:check" /> : <Icon icon="feather:copy" />}
      </Button>
    ) : (
      suffixIcon
    );

    return (
      <>
        <div
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          ref={internalRef}
          className={Classnames(style.outer, {
            [style.fill]: fill,
            [style.hasCopyButton]: copyButton,
          })}
        >
          <div
            className={classNameMerged}
            onMouseDown={(e) => {
              inputRef.current?.focus();
              if (e.target !== inputRef.current) e.preventDefault();
            }}
          >
            <span
              className={Classnames(style.preIcon, {
                hidden: typeof prefixIcon === "undefined",
              })}
            >
              {prefixIcon}
            </span>
            <div
              className={Classnames(style.label, {
                [style.smallLabel]: hasFocus || placeholder || value,
              })}
              data-testid={name ? `label-form-${name}` : undefined}
            >
              {label}
              {optional && (
                <span className={style.optional}>
                  {i18n.t("common:optional")}
                </span>
              )}
            </div>
            <input
              name={name}
              autoComplete={autocomplete}
              autoFocus={autoFocus}
              ref={inputRef}
              type={type}
              placeholder={placeholder}
              required={type === "search"}
              disabled={disabled}
              readOnly={readOnly}
              onBlur={onBlur}
              title=""
              onCompositionStart={() => setComposing(true)}
              onCompositionEnd={(e) => {
                let textValue: string | number = (e.target as HTMLInputElement)
                  .value;

                if (type === "number") {
                  textValue = Number(textValue);
                }
                setValue(textValue);

                if (typeof onChangeProp === "function") {
                  onChangeProp(textValue);
                }
                setComposing(false);
              }}
              onFocus={onFocus}
              onChange={onChange}
              onKeyDown={(e) => {
                if (
                  !isComposing &&
                  e.key === "Enter" &&
                  typeof onEnter === "function"
                ) {
                  onEnter(e);
                }
                if (typeof onKeyDownProp === "function") {
                  onKeyDownProp(e);
                }
              }}
              value={typeof value === "undefined" ? "" : value.toString()}
              data-testid={name ? `input-form-${name}` : undefined}
            />
            <Button
              as="div"
              className={style.clearbutton}
              onClick={() => {
                setValue(undefined);
                if (typeof onChangeProp === "function") onChangeProp("");
              }}
            >
              <Icon icon="feather:x-circle" />
            </Button>
            <span
              className={Classnames(style.sufIcon, {
                hidden: typeof suffixIconElement === "undefined",
              })}
            >
              {suffixIconElement}
            </span>
          </div>
          <div
            className={Classnames(style.hint, {
              [style.error]: error && hasBlurred,
              [style.success]: success,
              hidden:
                typeof hint === "undefined" &&
                (typeof error === "undefined" || !hasBlurred),
            })}
          >
            {error || hint}
          </div>
        </div>
        {copyButtonElement}
      </>
    );
  },
);

Textbox.displayName = "Textbox";

export default Textbox;
